/**
 * Product Restrictions Utility Module
 * Centralized logic for handling product restrictions across the site
 */

/**
 * Utility class for handling product restrictions
 */
class ProductRestrictionsManager {
  constructor(restrictedOneTimePurchaseProducts = [], oneUnitOnlyProducts = []) {
    // Ensure arrays are always arrays, even if null/undefined is passed
    this.restrictedOneTimePurchaseProducts = Array.isArray(restrictedOneTimePurchaseProducts) 
      ? restrictedOneTimePurchaseProducts 
      : [];
    this.oneUnitOnlyProducts = Array.isArray(oneUnitOnlyProducts) 
      ? oneUnitOnlyProducts 
      : [];
    
    // Debug logging if null/undefined values were passed
    if (!Array.isArray(restrictedOneTimePurchaseProducts)) {
      console.warn('ProductRestrictionsManager: restrictedOneTimePurchaseProducts is not an array:', restrictedOneTimePurchaseProducts);
    }
    if (!Array.isArray(oneUnitOnlyProducts)) {
      console.warn('ProductRestrictionsManager: oneUnitOnlyProducts is not an array:', oneUnitOnlyProducts);
    }
  }

  /**
   * Normalizes cart data structure from different sources (direct cart vs Rebuy cart)
   * @param {Object} cart - Cart data object
   * @returns {Array} - Normalized cart items array
   */
  normalizeCartItems(cart) {
    if (!cart) return [];
    
    if (typeof cart.items === 'function') {
      return cart.items();
    } else if (cart.items) {
      return cart.items;
    } else if (cart.cart?.items) {
      return cart.cart.items;
    }
    
    return [];
  }

  /**
   * Checks if a product is restricted for one-time purchase
   * @param {number} productId - Product ID to check
   * @returns {boolean} - Whether the product is restricted
   */
  isProductRestrictedOneTime(productId) {
    return this.restrictedOneTimePurchaseProducts?.some(product => product.id === productId) || false;
  }

  /**
   * Checks if a product allows only one unit
   * @param {number} productId - Product ID to check
   * @returns {boolean} - Whether the product allows only one unit
   */
  isProductOneUnitOnly(productId) {
    return this.oneUnitOnlyProducts?.some(product => product.id === productId) || false;
  }

  /**
   * Checks if a product is in any of the restriction lists
   * @param {number} productId - Product ID to check
   * @returns {boolean} - Whether the product has any restrictions
   */
  hasProductRestrictions(productId) {
    return this.isProductRestrictedOneTime(productId) || this.isProductOneUnitOnly(productId);
  }

  /**
   * Gets product data by ID from one-unit-only products
   * @param {number} productId - Product ID to find
   * @returns {Object|null} - Product data or null if not found
   */
  getOneUnitProductData(productId) {
    return this.oneUnitOnlyProducts?.find(product => product.id === productId) || null;
  }

  /**
   * Checks if a product is already in the cart
   * @param {number} productId - Product ID to check
   * @param {Object} cart - Cart data object
   * @returns {boolean} - Whether the product is in cart
   */
  isProductInCart(productId, cart) {
    const cartItems = this.normalizeCartItems(cart);
    return cartItems.some(item => item.product_id === productId);
  }

  /**
   * Determines if add-to-cart button should be disabled for a product
   * @param {number} productId - Product ID to check
   * @param {Object} cart - Cart data object
   * @returns {boolean} - Whether the button should be disabled
   */
  shouldDisableAddToCart(productId, cart) {
    // Always disable if product is restricted for one-time purchase
    if (this.isProductRestrictedOneTime(productId)) {
      return true;
    }
    
    // For one-unit-only products, only disable if already in cart
    if (this.isProductOneUnitOnly(productId)) {
      return this.isProductInCart(productId, cart);
    }
    
    // For regular products, allow normal behavior
    return false;
  }

  /**
   * Gets all one-unit-only products that are currently in the cart
   * @param {Object} cart - Cart data object
   * @returns {Array} - Array of one-unit-only products in cart
   */
  getOneUnitProductsInCart(cart) {
    const cartItems = this.normalizeCartItems(cart);
    return cartItems.filter(item => this.isProductOneUnitOnly(item.product_id));
  }

  /**
   * Fetches cart data from Shopify
   * @returns {Promise<Object>} - Cart data
   */
  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }

  /**
   * Refreshes cart data using Rebuy if available, otherwise falls back to page reload
   * @param {Function} callback - Optional callback to run after refresh
   */
  refreshCartData(callback) {
    if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchShopifyCart === 'function') {
      window.Rebuy.Cart.fetchShopifyCart((refreshedCart) => {
        if (callback) callback(refreshedCart);
      });
    } else {
      // Fallback to page reload if Rebuy method is not available
      window.location.reload();
    }
  }
}

// Export the manager class
window.ProductRestrictionsManager = ProductRestrictionsManager; 